import React from 'react';

import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import { faCircleCheck as faCircleCheckSolid } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons/faQuestionCircle';
import { faXmarkCircle } from '@fortawesome/pro-solid-svg-icons/faXmarkCircle';

import {
  NEXT_30_DAYS,
  PAST_30_DAYS,
} from 'components/Common/DateRangeFilter/constants';

import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';

import GoogleMeetLogo from 'modules/CounselorMeetingScheduler/assets/google-meet-logo.png';
import ZoomLogo from 'modules/CounselorMeetingScheduler/assets/zoom-logo.png';
import AppleCalendarImage from 'modules/EventSchedulerV2/assets/apple-cal.png';
import GoogleCalendarImage from 'modules/EventSchedulerV2/assets/google-cal.png';
import MicrosoftCalendarImage from 'modules/EventSchedulerV2/assets/microsoft-outlook.png';
import MicrosoftTeams from 'modules/EventSchedulerV2/K12Admin/assets/microsoft-teams-logo.png';

export const EVENT_SCHEDULE_LINK = '/events/event-schedule';
export const EVENT_TEMPLATES_LINK = '/events/event-templates';
export const BLOCKED_DATES_LINK = '/events/blocked-dates';
export const PAST_EVENTS_LINK = '/events/past-events';
export const PENDING_REQUESTS_LINK = '/events/pending-requests';
export const PARTNER_EVENTS_LINK = '/events/partner-events';

export const NAVIGATION_MAP = Object.freeze({
  1: EVENT_SCHEDULE_LINK,
  2: EVENT_TEMPLATES_LINK,
  3: BLOCKED_DATES_LINK,
  4: PAST_EVENTS_LINK,
  5: PENDING_REQUESTS_LINK,
  6: PARTNER_EVENTS_LINK,
});

export const NEW_EVENT_BUTTON_OPTIONS = [
  {
    title: 'Event',
    description: 'Schedule a unique, one-time event',
    url: '/events/event-schedule/new',
  },
  {
    title: 'Event template',
    description: 'Create a template and add time slots',
    url: '/events/event-templates/new',
  },
];

export const INTERNAL_EVENT_MANAGEMENT = 'internal_event_management';
export const COLLEGE_PARTNER_MANAGEMENT = 'college_partner_management';

export const REQUEST_CONFIRMED = 'approved';
export const REQUEST_DECLINED = 'rejected';
export const REQUEST_PENDING = 'requested';
export const REQUEST_REMOVED = 'removed';
export const NOT_REGISTERED = 'not_registered';

export const EVENT_TYPE_STATUS_MAP = Object.freeze({
  [REQUEST_CONFIRMED]: { label: 'REQUEST CONFIRMED', color: 'green' },
  [REQUEST_DECLINED]: { label: 'REQUEST DECLINED', color: 'red' },
  [REQUEST_PENDING]: { label: 'REQUEST PENDING', color: 'orange' },
});

export const EVENT_STATUS_BUTTON_MAP = Object.freeze({
  [REQUEST_CONFIRMED]: {
    label: 'Change to declined',
    color: 'red',
    hasIconRight: true,
    variant: 'outlined',
    icon: 'arrow',
  },
  [REQUEST_DECLINED]: {
    label: 'Change to confirmed',
    color: 'blue',
    hasIconRight: true,
    variant: 'outlined',
    icon: 'arrow',
  },
  [REQUEST_PENDING]: {
    label: 'Confirm request',
    color: 'gray',
    hasIconLeft: true,
    variant: 'outlined',
    icon: <FontAwesomeIcon icon={faCircleCheck} color="greenDark" />,
  },
});

export const CONFIRM_KEY = 'confirm';
export const DECLINE_KEY = 'decline';

export const REP_VISIT_FORMAT_KEY = 'rep_visit';
export const OTHER_FORMAT_KEY = 'other';
export const WORKSHOP_FORMAT_KEY = 'workshop';
export const VOLUNTEER_FORMAT_KEY = 'volunteer';
export const FIELD_TRIP_FORMAT_KEY = 'field_trip';
export const COLLEGE_REP_VISIT_FORMAT_KEY = 'college_rep_visit';
export const PANEL_FORMAT_KEY = 'panel';
export const FAIR_FORMAT_KEY = 'fair';

export const EVENT_TYPE_CHIP_MAP = Object.freeze({
  [OTHER_FORMAT_KEY]: {
    color: 'gray',
    label: 'Other',
  },
  [REP_VISIT_FORMAT_KEY]: {
    color: 'violet',
    label: 'Partner visit',
  },
  repVisit: {
    color: 'violet',
    label: 'Partner visit',
  },
  fair: {
    color: 'indigo',
    label: 'Fair',
  },
  panel: {
    color: 'yellow',
    label: 'Panel',
  },
  [WORKSHOP_FORMAT_KEY]: {
    color: 'teal',
    label: 'Workshop',
  },
  [VOLUNTEER_FORMAT_KEY]: {
    color: 'pink',
    label: 'Volunteer',
  },
  [FIELD_TRIP_FORMAT_KEY]: {
    color: 'green',
    label: 'Field Trip',
  },
  fieldTrip: {
    color: 'green',
    label: 'Field Trip',
  },
  [COLLEGE_REP_VISIT_FORMAT_KEY]: {
    color: 'orange',
    label: 'College Rep Visit',
  },
  collegeRepVisit: {
    color: 'orange',
    label: 'College Rep Visit',
  },
});

export const HIDDEN_EVENT_TYPES = ['other'];

export const ACCESS_KEY_LABEL_MAP = Object.freeze({
  students: 'Students',
  guardians: 'Guardians',
  college_reps: 'College partners',
  industry_partners: 'Industry partners',
});

export const EVENT_FORMAT_LIST = [
  {
    name: 'Fair',
    description: 'a large event with many representatives setting up tables',
    value: 'fair',
  },
  {
    name: 'Panel',
    description: 'a discussion with a small group of representatives',
    value: 'panel',
  },
  {
    name: 'Partner visit',
    description:
      'an event organized for a single representative or a small group of representatives from an organization',
    value: 'rep_visit',
  },
  {
    name: 'College rep visit',
    description:
      'an event organized for a single representative or a small group of representatives from a college',
    value: COLLEGE_REP_VISIT_FORMAT_KEY,
  },
  {
    name: 'Workshop',
    description:
      'an event where participants can engage in intensive discussion or activity on a particular subject',
    value: WORKSHOP_FORMAT_KEY,
  },
  {
    name: 'Volunteer',
    description:
      'an event where participants can offer time, effort and talent to a need or cause',
    value: VOLUNTEER_FORMAT_KEY,
  },
  {
    name: 'Field trip',
    description:
      'an event where participants visit a location for hands-on learning and experience outside the classroom',
    value: FIELD_TRIP_FORMAT_KEY,
  },
  {
    name: 'Other',
    description:
      'an event that has some other format, to be explained in detail in the description field',
    value: 'other',
  },
];

export const STUDENT_KEY = 'students';
export const GUARDIAN_KEY = 'guardians';
export const COLLEGE_REPS_KEY = 'college_reps';
export const INDUSTRY_PARTNER_KEY = 'industry_partners';
export const STAFF_KEY = 'k12_admins';
export const DISTRICT_INDUSTRY_PARTNER = 'district_industry_partner';
export const DISTRICT_COLLEGE_REP = 'district_college_rep';
export const DISTRICT_GUARDIAN = 'district_guardian';
export const ALL_PARTICIPANTS_KEY = 'all_participants';

export const EVENT_PARTICIPANT_LIST = [
  { label: 'Students', value: STUDENT_KEY },
  { label: 'Guardians', value: GUARDIAN_KEY },
  { label: 'College Partners', value: COLLEGE_REPS_KEY },
  { label: 'Industry Partners', value: INDUSTRY_PARTNER_KEY },
  { label: 'Staff', value: STAFF_KEY },
];

export const EXTERNAL_LINK_POPOVER_DATA = Object.freeze({
  description: `Want to add this event to a different Calendar? Follow the instructions here to learn how:`,
  logos: [
    {
      name: 'Zoom Meeting',
      logo: ZoomLogo,
      alt: 'zoom-meeting',
      href: 'https://www.notion.so/Zoom-Personal-Meeting-Link-Instructions-cdfdec26fdf94f36a6f6f09559305ae8',
    },
    {
      name: 'Microsoft Teams',
      logo: MicrosoftTeams,
      alt: 'microsoft-teams',
      href: 'https://www.notion.so/Microsoft-Teams-Personal-Meeting-Link-Instructions-e96b09cf0dc945e8940e44174e7f430b',
    },
    {
      name: 'Google Meet',
      logo: GoogleMeetLogo,
      alt: 'google-meet',
      href: 'https://www.notion.so/Google-Meet-Personal-Meeting-Link-Instructions-39f60c8c55c24cd4b2ebe4fe56254ce5',
    },
  ],
});

export const DAY_MAP = Object.freeze({
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
});

export const CONFIRMED_STATUS = 'confirmed';
export const DECLINED_STATUS = 'declined';
export const PENDING_STATUS = 'pending';
export const REGISTERED_STATUS = 'registered';
export const LOCKED_STATUS = 'locked';

export const IN_PERSON = 'in_person';
export const VIRTUAL = 'virtual';

export const ATTENDANCE_FORMAT_EMOJI_MAP = Object.freeze({
  in_person: ' 🚗',
  virtual: ' 🖥',
});

export const ATTENDANCE_FORMAT_LABEL_MAP = Object.freeze({
  [IN_PERSON]: 'In person',
  [VIRTUAL]: 'Virtual',
});

export const BENEFITS_LIST = [
  '1. Increased exposure - our students will be able to see your profile on SchooLinks to learn more about your partners before attending the visit.',
  '2. Visit attendance data - we will be able to enable registration and attendance tracking on our platform, and we can share some aggregated numbers upon request.',
  '3. You can book visits at other schools across the country.',
  '4. If you don’t see times that work for you, get updated when our district published new event openings.',
];

export const ALL_REPORTS_KEY = 'all';
export const COLLEGE_REPS_REPORTS_KEY = 'college_reps';
export const INDUSTRY_PARTNER_REPORTS_KEY = 'industry_partners';
export const STUDENT_REGISTRATIONS_REPORTS_KEY = 'student_registrations';
export const STAFF_REGISTRATIONS_REPORTS_KEY = 'staff_registrations';

export const TIME_ZONES = [
  {
    name: 'EST/IET',
    timeZone: 'America/New_York',
  },
  {
    name: 'CST',
    timeZone: 'America/Chicago',
  },
  {
    name: 'MST/PNT',
    timeZone: 'America/Denver',
  },
  {
    name: 'PST',
    timeZone: 'America/Los_Angeles',
  },
];

export const TEMPLATE_KEY = 'template';
export const EVENT_KEY = 'event';
export const PAST_EVENT_KEY = 'past_event';

export const PENDING_KEY = 'pending';

export const EVENT_CARD_COUNT_BADGES = {
  [STUDENT_KEY]: 'Students',
  [GUARDIAN_KEY]: 'Guardians',
  [COLLEGE_REPS_KEY]: 'College Partners',
  [INDUSTRY_PARTNER_KEY]: 'Industry Partners',
  [PENDING_KEY]: 'Pending requests',
  [STAFF_KEY]: 'Staff',
};

export const PAST_EVENT_CARD_COUNT_BADGES = ({ studentAttendeesCount }) => ({
  [STUDENT_KEY]: `Students ${
    studentAttendeesCount ? 'attended' : 'registered'
  }`,
  [GUARDIAN_KEY]: 'Guardians registered',
  [COLLEGE_REPS_KEY]: 'College partners confirmed',
  [INDUSTRY_PARTNER_KEY]: 'Industry partners confirmed',
  [STAFF_KEY]: 'Staff registered',
});

export const PAST_EMPLOYER_EVENT_CARD_COUNT_BADGES = {
  [STUDENT_KEY]: 'Students',
  [GUARDIAN_KEY]: 'Guardians',
  [STAFF_KEY]: 'Staff',
};

export const COUNT_BADGE_COLORS = ({ studentAttendeesCount }) => ({
  [COLLEGE_REPS_KEY]: 'green',
  [INDUSTRY_PARTNER_KEY]: 'green',
  k12_admins: 'green',
  [PENDING_KEY]: 'orange',
  [GUARDIAN_KEY]: 'gray',
  [STUDENT_KEY]: studentAttendeesCount ? 'green' : 'gray',
});

export const EVENT_FORMAT = 'EVENT_FORMAT';
export const EVENT_NAME = 'EVENT_NAME';
export const EVENT_DESCRIPTION = 'EVENT_DESCRIPTION';
export const EVENT_CAREER_CLUSTERS = 'EVENT_CAREER_CLUSTERS';
export const EVENT_ALLOW_MULTIPLE_PARTICIPANT =
  'EVENT_ALLOW_MULTIPLE_PARTICIPANT';
export const EVENT_CONTACTS = 'EVENT_CONTACTS';
export const EVENT_DATE_TIME = 'EVENT_DATE_TIME';
export const EVENT_LOCATION = 'EVENT_LOCATION';
export const EVENT_MEETING_LINK = 'EVENT_MEETING_LINK';
export const EVENT_PARTICIPANT_COUNT = 'EVENT_PARTICIPANT_COUNT';
export const EVENT_PARTICIPANT_DURATION = 'EVENT_PARTICIPANT_DURATION';
export const EVENT_PARTICIPANT_TYPE = 'EVENT_PARTICIPANT_TYPE';
export const EVENT_REQUIRE_MEETING_LINK = 'EVENT_REQUIRE_MEETING_LINK';
export const EVENT_SCOPES = 'EVENT_SCOPES';
export const NUMBER_OF_ORGS_TO_SHOW = 3;
export const NUMBER_OF_ORGS_TO_SHOW_POPOVER = 10;

export const K12ADMIN_API_PATH =
  'v1/event-scheduler/k12-admin/events/calendar-events/';
export const MEMBER_API_PATH =
  'v1/event-scheduler/member/events/calendar-events/';
export const K12_ADMIN_PARTNER_EVENT_API_PATH =
  'v1/event-scheduler/k12-admin/partner-events/calendar-events/';

export const MAX_LETTER_COUNT = 80;

export const SINGULAR_TIME_KEY = 'singular';
export const RANGE_TIME_KEY = 'range';
export const MEMBER_KEY = 'member';
export const K12_ADMIN_KEY = 'k12_admin';
export const ADDED_MANUALLY_LABEL = 'Added manually';

export const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const DISABLED_LINK_MESSAGE_STUDENTS = `Event is unconfirmed and not visible to students yet.`;
export const DISABLED_LINK_MESSAGE_ORGS =
  'This event is not open to college reps or industry partners.';

export const COLLEGE_REP_OPTION = {
  value: COLLEGE_REPS_KEY,
  type: DISTRICT_COLLEGE_REP,
  label: 'College partners',
};
export const INDUSTRY_PARTNER_OPTION = {
  value: INDUSTRY_PARTNER_KEY,
  type: DISTRICT_INDUSTRY_PARTNER,
  label: 'Industry partners',
};
export const INVITE_EXTERNAL_ORG_OPTIONS = [
  COLLEGE_REP_OPTION,
  INDUSTRY_PARTNER_OPTION,
];

export const COLLEGE_REP_INVITE_MAIL_STEPS = [
  `Go to `,
  'Click the button to add an employer',
  'Search for the college you represent',
  'Start scheduling events',
];

export const INDUSTRY_PARTNER_INVITE_MAIL_STEPS = [
  `Go to `,
  'Click the button to add an employer',
  'Search for the company you represent',
];

export const INVITE_EVENT_STEP_MAP = Object.freeze({
  [COLLEGE_REPS_KEY]: COLLEGE_REP_INVITE_MAIL_STEPS,
  [INDUSTRY_PARTNER_KEY]: INDUSTRY_PARTNER_INVITE_MAIL_STEPS,
});

export const ACCESS_TYPE_EDIT_KEY = 'edit';
export const ACCESS_TYPE_FULL_KEY = 'full';
export const ACCESS_TYPE_VIEW_KEY = 'view';

export const SURVEY_TYPE = 'survey';
export const ORGANIZATION_REGISTRATION_SURVEY_TYPE =
  'organization_registration_form';
export const STUDENT_REGISTRATION_SURVEY_TYPE = 'student_registration_form';
export const INSTRUCTION_TYPE = 'instructions';
export const EMAIL_TYPE = 'email';
export const EMAIL_URL_MESSAGE =
  'Click here to access the latest event details';

export const DEADLINE_TO_UNREGISTER_FOR_MEMBER = 3; // member can't unregister if there is less than 3 days are left for the event

export const THANK_YOU_EMAIL_KEY = 'event_scheduler_thank_you_email';
export const CONFIRMATION_EMAIL_KEY = 'event_scheduler_accepting_email';

export const STUDENT_REGISTRATION_FORM = `student_registration_form`;
export const STUDENT_FOLLOW_UP_SURVEY = `student_follow_up`;
export const ORGANIZATION_REGISTRATION_FORM = `organization_registration_form`;
export const ORGANIZATION_FOLLOW_UP_SURVEY = `organization_follow_up`;

export const K12_ADMIN_SUPPORT_LINK =
  'https://support.schoolinks.com/en/articles/4513084-staff-how-to-subscribe-to-schoolinks-events-in-your-personal-calendar';
export const MEMBER_SUPPORT_LINK =
  'https://support.schoolinks.com/en/articles/10459583-industry-partner-overview-of-events';
export const STUDENT_SUPPORT_LINK =
  'https://support.schoolinks.com/en/articles/6616891-student-how-to-subscribe-to-schoolinks-events-in-your-personal-calendar';

export const GET_INSTRUCTION_FILES_QUERY = 'instruction_files_query';
export const GET_EVENT_EMAILS_QUERY = 'event_emails_query';
export const GET_EVENT_SURVEYS_QUERY = 'event_surveys_query';
export const SCHOOL_TYPE = 'schools';
export const GRADE_TYPE = 'grades';
export const TAG_TYPE = 'tags';
export const STUDENT_USER = 'student';
export const GUARDIAN_USER = 'guardian';
export const MEMBER_USER = 'member';
export const K12_ADMIN_USER = 'k12Admin';

export const MEMBER_SIDE_BAR_FILTERS = [
  {
    label: 'Registered',
    value: REQUEST_CONFIRMED,
    badgeProps: {
      color: 'green',
      variant: 'filled',
    },
  },
  {
    label: 'Pending',
    value: REQUEST_PENDING,
    badgeProps: {
      color: 'orange',
      variant: 'filled',
    },
  },
  {
    label: 'Declined',
    value: REQUEST_DECLINED,
    badgeProps: {
      color: 'red',
      variant: 'filled',
    },
  },
];

export const NUMBER_OF_EVENTS_TO_SHOW = 3;

export const MEMBER_EVENTS_SEARCH_THRESHOLD = 2;
export const SCHOOL_KEY = 'School';
export const DISTRICT_KEY = 'District';

export const HE_SCHOOL_KEY = 'he_school';
export const EMPLOYER_KEY = 'employer';

export const EMAIL_ERROR_MESSAGE = 'Email is already in use.';
export const EMAIL_INVALID_MESSAGE = 'Enter a valid email address.';
export const MEMBER_EMAIL_ERROR_MESSAGE =
  'Email is associated with an existing member account.';
export const CUSTOM_EMAIL_ERROR_MESSAGE =
  'This email is associated with an account. Please log in to register.';

export const UPCOMING_EVENTS_TAB_KEY = 1;
export const EVENT_OPENINGS_TAB_KEY = 2;
export const HOST_EVENTS_TAB_KEY = 3;

export const STATES = [
  { label: 'Alabama', id: 'Alabama' },
  { label: 'Alaska', id: 'Alaska' },
  { label: 'Arizona', id: 'Arizona' },
  { label: 'Arkansas', id: 'Arkansas' },
  { label: 'California', id: 'California' },
  { label: 'Colorado', id: 'Colorado' },
  { label: 'Connecticut', id: 'Connecticut' },
  { label: 'Delaware', id: 'Delaware' },
  { label: 'Florida', id: 'Florida' },
  { label: 'Georgia', id: 'Georgia' },
  { label: 'Hawaii', id: 'Hawaii' },
  { label: 'Idaho', id: 'Idaho' },
  { label: 'Illinois', id: 'Illinois' },
  { label: 'Indiana', id: 'Indiana' },
  { label: 'Iowa', id: 'Iowa' },
  { label: 'Kansas', id: 'Kansas' },
  { label: 'Kentucky', id: 'Kentucky' },
  { label: 'Louisiana', id: 'Louisiana' },
  { label: 'Maine', id: 'Maine' },
  { label: 'Maryland', id: 'Maryland' },
  { label: 'Massachusetts', id: 'Massachusetts' },
  { label: 'Michigan', id: 'Michigan' },
  { label: 'Minnesota', id: 'Minnesota' },
  { label: 'Mississippi', id: 'Mississippi' },
  { label: 'Missouri', id: 'Missouri' },
  { label: 'Montana', id: 'Montana' },
  { label: 'Nebraska', id: 'Nebraska' },
  { label: 'Nevada', id: 'Nevada' },
  { label: 'New Hampshire', id: 'New Hampshire' },
  { label: 'New Jersey', id: 'New Jersey' },
  { label: 'New Mexico', id: 'New Mexico' },
  { label: 'New York', id: 'New York' },
  { label: 'North Carolina', id: 'North Carolina' },
  { label: 'North Dakota', id: 'North Dakota' },
  { label: 'Ohio', id: 'Ohio' },
  { label: 'Oklahoma', id: 'Oklahoma' },
  { label: 'Oregon', id: 'Oregon' },
  { label: 'Pennsylvania', id: 'Pennsylvania' },
  { label: 'Rhode Island', id: 'Rhode Island' },
  { label: 'South Carolina', id: 'South Carolina' },
  { label: 'South Dakota', id: 'South Dakota' },
  { label: 'Tennessee', id: 'Tennessee' },
  { label: 'Texas', id: 'Texas' },
  { label: 'Utah', id: 'Utah' },
  { label: 'Vermont', id: 'Vermont' },
  { label: 'Virginia', id: 'Virginia' },
  { label: 'Washington', id: 'Washington' },
  { label: 'West Virginia', id: 'West Virginia' },
  { label: 'Wisconsin', id: 'Wisconsin' },
  { label: 'Wyoming', id: 'Wyoming' },
];

export const EVENT_FORMAT_FILTER_OPTIONS = [
  { label: 'Fair', id: 'fair', color: 'indigo' },
  { label: 'Panel', id: 'panel', color: 'yellow' },
  { label: 'Partner visit', id: 'rep_visit', color: 'violet' },
  {
    label: 'College rep visit',
    id: COLLEGE_REP_VISIT_FORMAT_KEY,
    color: 'orange',
  },
  { label: 'Workshop', id: 'workshop', color: 'teal' },
  { label: 'Volunteer', id: 'volunteer', color: 'pink' },
  { label: 'Field trip', id: FIELD_TRIP_FORMAT_KEY, color: 'green' },
  { label: 'Other', id: 'other' },
];

export const EVENT_LOCATION_FILTER_OPTIONS = [
  { label: 'In-person 🚗', id: 'in_person' },
  { label: 'Virtual 🖥', id: 'virtual' },
];

export const AFFILIATION_KEY = 'affiliation';
export const CRM_AFFILIATION_KEY = 'crm_affiliation';
export const AFFILIATION_FILTER_KEY = 'confirmed_affiliations';
export const CRM_AFFILIATION_FILTER_KEY = 'confirmed_crm_affiliations';

export const EVENTS_KEY = 'events';
export const PARTNER_EVENTS_KEY = 'partner_events';
export const PAST_EVENTS_KEY = 'past_events';

export const PARTNER_EVENT_CSV_LIST = [
  {
    value: ALL_REPORTS_KEY,
    label: 'All events and contacts',
  },
  {
    value: STUDENT_REGISTRATIONS_REPORTS_KEY,
    label: 'Student registrations',
  },
];

export const PAST_EVENT_CSV_LIST = [
  {
    value: ALL_REPORTS_KEY,
    label: 'All events and contacts',
  },
  {
    value: STUDENT_REGISTRATIONS_REPORTS_KEY,
    label: 'Student registrations and attendance',
  },
  {
    value: STAFF_REGISTRATIONS_REPORTS_KEY,
    label: 'Staff registrations and attendance',
  },
];

export const EVENT_CSV_LIST = [
  {
    value: ALL_REPORTS_KEY,
    label: 'All events and contacts',
  },
  {
    value: INDUSTRY_PARTNER_REPORTS_KEY,
    label: 'Industry Partner events and contacts',
  },
  {
    value: COLLEGE_REPS_REPORTS_KEY,
    label: 'College Partner events and contacts',
  },
  {
    value: STUDENT_REGISTRATIONS_REPORTS_KEY,
    label: 'Student registrations',
  },
  {
    value: STAFF_REGISTRATIONS_REPORTS_KEY,
    label: 'Staff registrations',
  },
];

export const EXPORT_CSV_MAP = {
  [EVENTS_KEY]: EVENT_CSV_LIST,
  [PARTNER_EVENTS_KEY]: PARTNER_EVENT_CSV_LIST,
  [PAST_EVENTS_KEY]: PAST_EVENT_CSV_LIST,
};

export const MEMBER_EVENT_MGMT_CAPABILITY = 'organization_event_management';

export const MEMBER_EVENT_DISTRICT_PAGE_DATE_GROUP_FORMAT = 'YYYYMMDD';

export const ATTENDEE_TYPE_MAP = {
  [K12_ADMIN_KEY]: 'Staff',
  [GUARDIAN_USER]: 'Guardian',
  [STUDENT_USER]: 'Student',
};

export const ALL_DISTRICTS_RADIO_KEY = '0';
export const DISTRICTS_WITHIN_RADIUS_RADIO_KEY = '1';
export const SELECTED_DISTRICTS_RADIO_KEY = '2';

export const ELIGIBLE_DISTRICT_RADIO_OPTIONS = [
  {
    value: ALL_DISTRICTS_RADIO_KEY,
    label: 'All districts',
  },
  {
    value: DISTRICTS_WITHIN_RADIUS_RADIO_KEY,
    label: 'Districts within a set radius',
  },
  {
    value: SELECTED_DISTRICTS_RADIO_KEY,
    label: 'Selected districts',
  },
];

export const MILE_RADIUS_OPTIONS = [10, 25, 50, 75, 100, 150, 200];

export const ALL_SCHOOL_ACCESS = 'all';
export const SPECIFIC_SCHOOL_ACCESS = 'specific';

export const CALENDAR_LIST = [
  { name: 'Apple Calendar', image: AppleCalendarImage },
  { name: 'Microsoft Outlook', image: MicrosoftCalendarImage },
  { name: 'Google Calendar', image: GoogleCalendarImage },
];

export const EVENT_LOCATION_TYPE = 'location';
export const EVENT_VIRTUAL_URL_TYPE = 'virtual-url';

export const EVENT_LOCATION_FIELD_CHIPS = [
  { value: EVENT_LOCATION_TYPE, label: 'In person' },
  { value: EVENT_VIRTUAL_URL_TYPE, label: 'Virtual' },
];

export const LIST_DEFAULT_ORDER_BY = 'start_datetime,created';

export const DEFAULT_DATE_RANGE = NEXT_30_DAYS;
export const DEFAULT_PAST_DATE_RANGE = PAST_30_DAYS;
export const DISTRICT_INITIAL_EVENTS_COUNT = 3;

export const EVENT_APPLICATION_STATUS_MAP = {
  [REQUEST_CONFIRMED]: {
    color: 'greenDark',
    icon: faCircleCheckSolid,
    label: 'APPROVED',
  },
  [REQUEST_PENDING]: {
    color: 'orangeDark',
    icon: faQuestionCircle,
    label: 'PENDING',
  },
  [REQUEST_DECLINED]: {
    color: 'redDark',
    icon: faXmarkCircle,
    label: 'DECLINED',
  },
};

export const MAXIMUM_PARTICIPANT_COUNT_REACHED_ERROR =
  'Student max participants reached.';
export const REGISTRATION_TIME_EXPIRED_ERROR =
  'Registration time expired for this event.';

export const EVENT_LIST_VIEW_TYPE = Object.freeze({
  Calendar: 'calendar',
  List: 'list',
});

export const CALENDAR_COLOR_MAP = {
  [FAIR_FORMAT_KEY]: 'indigoXtraDark',
  [COLLEGE_REP_VISIT_FORMAT_KEY]: 'orangeXtraDark',
  [REP_VISIT_FORMAT_KEY]: 'violetXtraDark',
  [PANEL_FORMAT_KEY]: 'yellowXtraDark',
  [WORKSHOP_FORMAT_KEY]: 'tealXtraDark',
  [VOLUNTEER_FORMAT_KEY]: 'pinkXtraDark',
  [FIELD_TRIP_FORMAT_KEY]: 'greenXtraDark',
  [OTHER_FORMAT_KEY]: 'blueXtraDark',
};

export const K12_ADMIN_REGISTRATION_DISABLED_STATUSES = {
  OPEN: 'open',
  IS_PAST: 'is_past',
  CUTOFF_PASSED: 'cutoff_passed',
  MAX_PARTICIPANTS_REACHED: 'max_participants_reached',
};
